import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../../../services/apiClient';
import KalshiSettledFigures from './KalshiSettledFigures';
import NovigDailyBalances from './NovigDailyBalances';
import { balanceName } from './balanceOverviewModel';
import { accountLabel, isVisibleAccount } from './accountPresentation';

const usd = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
const eastern = value => new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'medium' }).format(new Date(value));

export default function NovigBalances({ provider = 'novig', title, accountId }) {
  const [snapshot, setSnapshot] = useState(null);
  const [dailyReport, setDailyReport] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const active = useRef(false);
  const mounted = useRef(false);
  const load = useCallback(async () => {
    if (active.current) return;
    active.current = true;
    setLoading(true);
    try {
      const response = await apiClient.get(`/${provider}/balances`, { timeout: 25000, ...(accountId ? { params: { accountId } } : {}) });
      if (mounted.current) { setSnapshot(response.data.data); setError(''); }
    } catch {
      if (mounted.current) setError('Unable to refresh balances. Please try again.');
    } finally {
      active.current = false;
      if (mounted.current) setLoading(false);
    }
  }, [provider, accountId]);

  useEffect(() => {
    mounted.current = true;
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, 30000);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
  return () => { mounted.current = false; clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [load]);

  const visibleSnapshot = snapshot && accountId ? (() => {
    const account = snapshot.accounts.find(row => row.id === accountId);
    return account ? { ...snapshot, accounts: [account], totals: account } : null;
  })() : snapshot;

  return (
    <section aria-labelledby="novig-title" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 id="novig-title" className="text-2xl font-semibold text-slate-900">{title || balanceName(provider)}</h1>
          <p className="mt-1 text-sm text-slate-500">Cash + original cost of open positions{accountId ? '.' : ', across every account.'}</p>
        </div>
        <button type="button" className="btn-primary" disabled={loading} onClick={load}>{loading ? 'Refreshing…' : 'Refresh balances'}</button>
      </header>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500" aria-live="polite">
        <span>Refreshes every 30 seconds</span>
        {visibleSnapshot && <span>Updated {eastern(visibleSnapshot.updatedAt)} ET</span>}
      </div>
      {error && <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{error}{visibleSnapshot && ' The last successful balances remain below.'}</div>}
      {snapshot && accountId && !visibleSnapshot && <p role="alert">This account was not returned in the latest balance refresh.</p>}
      {!visibleSnapshot && loading && <p role="status" className="py-12 text-center text-slate-500">Loading your accounts…</p>}
      {visibleSnapshot && <>
        <div className="grid gap-4 md:grid-cols-3">
          {[['Total balance', visibleSnapshot.totals.total], ['Cash', visibleSnapshot.totals.cash], ['Open positions at cost', visibleSnapshot.totals.positionCost]].map(([label, value], index) => (
            <div key={label} className={`rounded-xl border p-5 ${index === 0 ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
              <p className="text-sm text-slate-500">{label}</p>
              <p className="mt-2 text-3xl font-semibold tabular-nums text-slate-900">{usd(value)}</p>
            </div>
          ))}
        </div>
        {visibleSnapshot.cashBreakdown && <p className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">Wallet cash: {usd(visibleSnapshot.cashBreakdown.walletCash)} · Claimable winnings: {usd(visibleSnapshot.cashBreakdown.claimableWinnings)}. Cash includes both; claiming a payout moves it into the wallet without counting the winnings twice. <a className="underline" href={`https://polygonscan.com/address/${visibleSnapshot.walletAddress}`} target="_blank" rel="noreferrer">View wallet</a></p>}
        {Number(visibleSnapshot.walletBreakdown?.restrictedBonus || 0) > 0 && <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Wallet cash includes {usd(visibleSnapshot.walletBreakdown.restrictedBonus)} in restricted promotional bonus funds. Withdrawable cash: {usd(visibleSnapshot.walletBreakdown.withdrawableCash)}. Reconciliation uses the full wallet balance.</p>}
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <caption className="sr-only">Live balances by account, in US dollars</caption>
            <thead className="bg-slate-50 text-slate-500"><tr>
              <th scope="col" className="p-4 text-left">Account</th>
              <th scope="col" className="p-4 text-right">Cash</th>
              <th scope="col" className="p-4 text-right">Position cost</th>
              <th scope="col" className="p-4 text-right">Total balance</th>
            </tr></thead>
            <tbody>{visibleSnapshot.accounts.filter(isVisibleAccount).map(account => <tr key={account.id} className="border-t border-slate-100">
              <th scope="row" className="p-4 text-left font-medium text-slate-900">{accountLabel(account)}<span className="mt-1 block text-xs font-normal text-slate-400">{account.openPositions} open {account.openPositions === 1 ? 'position' : 'positions'}</span></th>
              <td className="whitespace-nowrap p-4 text-right tabular-nums">{usd(account.cash)}</td>
              <td className="whitespace-nowrap p-4 text-right tabular-nums">{usd(account.positionCost)}</td>
              <td className="whitespace-nowrap p-4 text-right font-semibold tabular-nums">{usd(account.total)}</td>
            </tr>)}</tbody>
            <tfoot className="border-t border-slate-200 bg-slate-50 font-semibold"><tr>
              <th scope="row" className="p-4 text-left">{accountId ? 'Account total' : 'All accounts'}</th>
              <td className="p-4 text-right tabular-nums">{usd(visibleSnapshot.totals.cash)}</td>
              <td className="p-4 text-right tabular-nums">{usd(visibleSnapshot.totals.positionCost)}</td>
              <td className="p-4 text-right tabular-nums">{usd(visibleSnapshot.totals.total)}</td>
            </tr></tfoot>
          </table>
        </div>
        <p className="text-xs text-slate-400">{provider.startsWith('polymarket-') ? 'Cash includes wallet pUSD and resolved, unclaimed winnings. Position cost is the original fee-exclusive cost of unresolved shares. ' : ['prophetx', 'yan-prophetx', 'ant-prophetx', 'sonya-prophetx'].includes(provider) ? 'Cash already includes funds reserved for unmatched orders. Position cost is the original filled stake; exposure credits are excluded. ' : ['novig', 'ant-novig'].includes(provider) ? 'Position cost deducts payouts already received for offsetting positions. ' : ['greg-polyus','polyus-dk','polyus-fido-ct','ant-polyus-inst'].includes(provider) ? 'Cash excludes collateral committed to short positions; position cost uses original acquisition cost. ' : 'Position cost uses Kalshi’s original market exposure. '}{provider === 'polyus-dk' && 'Position cost includes acquisition fees reported by PolyUS; settled figures show those fees separately. '}Totals use full precision before rounding to cents.</p>
      </>}
      <KalshiSettledFigures provider={provider} accountId={accountId} closingReport={dailyReport} />
      <NovigDailyBalances provider={provider} accountId={accountId} onData={setDailyReport} />
    </section>
  );
}
